import React, { useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { FileSpreadsheet, CalendarRange } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { reportDaily, formatApiError } from "@/lib/api";
import { unitOf, formatDateTime } from "@/lib/fabricData";

export function ReportsTab({ token }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!from || !to) return toast.error("Pick a start and end date");
    if (from > to) return toast.error("Start date must be before end date");
    setBusy(true);
    try {
      const data = await reportDaily(token, from, to);
      const wb = XLSX.utils.book_new();

      const oHead = ["Order ID", "Date", "Customer", "Company", "Order Status", "Fabric", "Colour", "Qty Ordered", "Qty Delivered", "Unit", "Item Status"];
      const oRows = [oHead, ...(data.orders || []).map((o) => [
        o.order_id, o.date ? formatDateTime(o.date) : "", o.client_name, o.company_name, o.status,
        o.fabric, o.color, o.quantity_kg, o.delivered_kg ?? "", unitOf(o.fabric), o.item_status,
      ])];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(oRows), "Orders");

      const iHead = ["Date", "Fabric", "Colour", "Quantity", "Unit", "Remark"];
      const iRows = [iHead, ...(data.incomings || []).map((r) => [
        r.date ? formatDateTime(r.date) : "", r.fabric, r.color, r.quantity, r.unit || "kg", r.remark || "",
      ])];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(iRows), "Stock Incomings");

      const gHead = ["Date", "Fabric", "Colour", "Quantity Deducted", "Order", "Company"];
      const gRows = [gHead, ...(data.outgoings || []).map((r) => [
        r.date ? formatDateTime(r.date) : "", r.fabric, r.color, r.quantity, r.order_ref || "", r.order_company || "",
      ])];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(gRows), "Stock Outgoings");

      XLSX.writeFile(wb, `daily-sheet-${from}_to_${to}.xlsx`);
      toast.success(`Daily sheet: ${oRows.length - 1} order lines, ${iRows.length - 1} in, ${gRows.length - 1} out`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="reports-panel">
      <div>
        <h2 className="font-display font-black text-2xl tracking-tight text-white">Reports</h2>
        <p className="text-sm text-white/40">Download an Excel Daily Sheet for a date range — orders received, stock incomings (received lots) and stock outgoings (approved orders).</p>
      </div>
      <div className="border border-white/10 bg-[#0D0D0D] p-5 max-w-xl">
        <div className="flex items-center gap-2 mb-4 text-white/70"><CalendarRange className="h-4 w-4" /><span className="text-sm uppercase tracking-wider">Daily Sheet</span></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-daily-from" className="rounded-none h-10 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-daily-to" className="rounded-none h-10 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
          </div>
        </div>
        <Button onClick={download} disabled={busy} data-testid="report-daily-download" className="mt-4 rounded-none h-10 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider disabled:opacity-50">
          <FileSpreadsheet className="h-4 w-4 mr-2" /> {busy ? "Preparing…" : "Download Daily Sheet"}
        </Button>
      </div>
    </div>
  );
}
